import React, { useState, useEffect } from 'react';
import { Crown, Search, AlertCircle, CheckCircle2, X } from 'lucide-react';
import { supabase } from '../../utils/supabase';

interface UserRow {
  user_id: string;
  email: string;
  is_premium: boolean;
  is_admin: boolean;
  created_at: string;
}

export function PremiumUsers() {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    const { data, error } = await supabase
      .from('profiles')
      .select('user_id, email, is_premium, is_admin, created_at')
      .order('created_at', { ascending: false });

    if (error) {
      setError('Failed to load users');
    } else {
      setUsers(data || []);
    }
    setLoading(false);
  };

  const handleTogglePremium = async (userId: string, currentValue: boolean) => {
    setToggling(userId);
    setError(null);
    setSuccess(null);

    const { error } = await supabase
      .from('profiles')
      .update({ is_premium: !currentValue })
      .eq('user_id', userId);

    if (error) {
      setError('Failed to update premium status');
    } else {
      setUsers(prev =>
        prev.map(u => u.user_id === userId ? { ...u, is_premium: !currentValue } : u)
      );
      setSuccess(`Premium status ${!currentValue ? 'granted' : 'removed'} successfully`);
      setTimeout(() => setSuccess(null), 3000);
    }
    setToggling(null);
  };

  const filtered = users.filter(u =>
    !search || (u.email || '').toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-14 bg-gray-200 rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex items-center gap-3 mb-6">
          <Crown className="h-6 w-6 text-amber-500" />
          <h2 className="text-2xl font-bold text-gray-900">Premium Users</h2>
          <span className="ml-auto text-sm text-gray-500">
            {users.filter(u => u.is_premium).length} premium / {users.length} total
          </span>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
            {success}
          </div>
        )}

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by email..."
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="space-y-2">
          {filtered.map(user => (
            <div
              key={user.user_id}
              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  user.is_premium ? 'bg-amber-100' : 'bg-gray-200'
                }`}>
                  {user.is_premium
                    ? <Crown className="h-4 w-4 text-amber-600" />
                    : <span className="text-xs font-bold text-gray-500">
                        {(user.email || '?')[0].toUpperCase()}
                      </span>
                  }
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate">{user.email || '(no email)'}</p>
                  <div className="flex items-center gap-2">
                    {user.is_admin && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Admin</span>
                    )}
                    {user.is_premium && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Premium</span>
                    )}
                    <span className="text-xs text-gray-400">
                      Joined {new Date(user.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => handleTogglePremium(user.user_id, user.is_premium)}
                disabled={toggling === user.user_id}
                className={`ml-4 flex-shrink-0 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  user.is_premium
                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                    : 'bg-gray-200 text-gray-700 hover:bg-amber-100 hover:text-amber-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {toggling === user.user_id
                  ? '...'
                  : user.is_premium ? 'Remove Premium' : 'Grant Premium'
                }
              </button>
            </div>
          ))}

          {filtered.length === 0 && (
            <p className="text-center text-gray-500 py-8">No users found</p>
          )}
        </div>
      </div>
    </div>
  );
}
